/**
 * Promotion Export Service
 * Builds and downloads an .xlsx workbook for selected promotions.
 * Single flat sheet: one row per (promotion x item), promotion metadata repeated
 * per row, dynamic step columns (final price + discount vs the regular price).
 * All prices are ex-VAT, matching how they are stored in Firestore.
 */
import { collection, query, where, documentId, getDocs } from 'firebase/firestore'
import { getFirebaseDb } from '../firebase/firebase.js'
import { getDocument } from '../firebase/firestore.js'
import { getSaleById } from './sales.js'
import { formatDate } from '../utils/formatting.js'
import logger from './logger.js'

const log = logger.createScoped('PromotionExport')

// xlsx is heavy (~300KB min) and this view is eagerly routed — load it on demand
// so it stays out of the main bundle (which is capped by the PWA precache limit)
let xlsxModule = null
async function loadXlsx() {
  if (!xlsxModule) {
    xlsxModule = await import('xlsx')
  }
  return xlsxModule
}

const PRICE_LIST_ID = 'בסיס'
const FIRESTORE_IN_QUERY_LIMIT = 30
const MAX_STEPS = 5

// Fixed column indexes (Group A + Group B); step columns follow from STEP_BASE_COL
const MAX_QTY_COL = 9
const PROMO_IMAGE_COL = 12
const ITEMS_COUNT_COL = 13
const ITEM_IMAGE_COL = 18
const REGULAR_PRICE_COL = 19
const STEP_BASE_COL = 20

// ============================================
// Helpers
// ============================================

function bool2he(v) {
  return v ? 'כן' : 'לא'
}

/**
 * Parse to a number, or null when empty/non-numeric (keeps cells blank, never 0/NaN)
 */
function num(v) {
  if (v === null || v === undefined || v === '') return null
  const n = typeof v === 'number' ? v : parseFloat(v)
  return Number.isNaN(n) ? null : n
}

function saleTypeLabel(saleType) {
  const types = {
    madregot: 'מדרגות',
    percentage: 'אחוז הנחה',
    fixed: 'סכום קבוע',
    bundle: 'חבילה',
  }
  return types[saleType] || saleType || ''
}

/**
 * Prefer the stored YYYY-MM-DD string; fall back to the Timestamp
 */
function promoDate(strDate, tsDate) {
  if (strDate) return strDate
  if (tsDate) return formatDate(tsDate, { fallback: '' })
  return ''
}

function chunkArray(array, size) {
  const chunks = []
  for (let i = 0; i < array.length; i += size) {
    chunks.push(array.slice(i, i + size))
  }
  return chunks
}

/**
 * Batch-read Catalog docs by id (30 per `in` query) and collect barcodes
 * @param {string[]} makats
 * @returns {Promise<Map<string, string>>}
 */
async function fetchBarcodesByMakats(makats) {
  const result = new Map()
  if (!makats.length) return result

  const db = getFirebaseDb()
  const catalogRef = collection(db, 'Catalog')
  const chunks = chunkArray(makats, FIRESTORE_IN_QUERY_LIMIT)

  const snapshots = await Promise.all(
    chunks.map((chunk) => getDocs(query(catalogRef, where(documentId(), 'in', chunk))))
  )

  for (const snapshot of snapshots) {
    snapshot.forEach((docSnap) => {
      const barcode = docSnap.data()?.barcode
      if (barcode) result.set(docSnap.id, String(barcode))
    })
  }
  return result
}

/**
 * Highest step index (1-5) that holds a valid step (positive-int amount, same
 * parse rule as promoCalc's getPromoSteps) across all promos. Bounds how many
 * step-column groups the sheet emits.
 */
function getStepColumnCount(promos) {
  let maxIndex = 0
  for (const promo of promos) {
    for (let i = 1; i <= MAX_STEPS; i++) {
      const amount = parseInt(promo[`step_${i}`]?.amount, 10)
      if (!Number.isNaN(amount) && amount > 0 && i > maxIndex) maxIndex = i
    }
  }
  return maxIndex
}

function setNumberFormat(XLSX, ws, r, c, z) {
  const cell = ws[XLSX.utils.encode_cell({ r, c })]
  if (cell && cell.t === 'n') cell.z = z
}

// ============================================
// Public API
// ============================================

/**
 * Build and download an .xlsx for the given promotions.
 * @param {string[]} saleIds
 * @param {{ merakez?: string, merakezName?: string }} [opts]
 * @returns {Promise<number>} number of promotions actually exported
 */
export async function exportPromotionsToExcel(saleIds, opts = {}) {
  const ids = Array.isArray(saleIds) ? saleIds.filter(Boolean) : []
  if (!ids.length) return 0

  log.info('Exporting promotions to Excel', { count: ids.length })

  const XLSX = await loadXlsx()

  // Full docs — the list feed is trimmed (no step_1..5 / max / orderDay)
  const promos = (await Promise.all(ids.map((id) => getSaleById(id)))).filter(Boolean)
  if (!promos.length) {
    throw new Error('No promotions found for export')
  }

  // Regular prices from the base price list; on failure export with blank price cells
  const priceByMakat = new Map()
  try {
    const priceList = await getDocument('Prices', PRICE_LIST_ID)
    for (const entry of priceList?.items || []) {
      const price = num(entry?.price)
      if (entry?.makat && price !== null) priceByMakat.set(entry.makat, price)
    }
  } catch (error) {
    log.warn('Failed to load base price list, exporting without regular prices', error)
  }

  // Barcodes from Catalog; on failure export with blank barcode cells
  const uniqueMakats = [
    ...new Set(promos.flatMap((p) => (p.items || []).map((item) => item?.makat).filter(Boolean))),
  ]
  let barcodeByMakat = new Map()
  try {
    barcodeByMakat = await fetchBarcodesByMakats(uniqueMakats)
  } catch (error) {
    log.warn('Failed to load barcodes, exporting without them', error)
  }

  const stepCount = getStepColumnCount(promos)

  const headers = [
    'מזהה מבצע',
    'שם המבצע',
    'תיאור',
    'מרכז',
    'סוג מבצע',
    'פעיל',
    'מאושר',
    'מצטרפים חדשים',
    'יום הזמנה',
    'הטבה שבועית',
    'הגבלת כמות (max)',
    'תאריך התחלה',
    'תאריך סיום',
    'קישור תמונת מבצע',
    'מס׳ פריטים במבצע',
    'מק״ט (SKU)',
    'ברקוד',
    'שם פריט',
    'גודל אריזה',
    'קישור תמונת פריט',
    'מחיר רגיל ₪ (לא כולל מע״מ)',
  ]
  for (let k = 1; k <= stepCount; k++) {
    headers.push(
      `מדרגה ${k} — כמות מצטברת`,
      `מדרגה ${k} — מחיר סופי ₪`,
      `מדרגה ${k} — הנחה ₪ ליח׳`,
      `מדרגה ${k} — הנחה %`
    )
  }

  const rows = []
  for (const promo of promos) {
    const promoCells = [
      promo.id || '',
      promo.label || '',
      promo.description || '',
      promo.merakez || '',
      saleTypeLabel(promo.saleType),
      bool2he(promo.active),
      bool2he(promo.approved),
      bool2he(promo.first_order),
      bool2he(promo.orderDay),
      bool2he(promo.weeklyBenefit),
      num(promo.max) ?? 'ללא הגבלה',
      promoDate(promo.start_date, promo.date_start_date),
      promoDate(promo.end_date, promo.date_end_date),
      promo.image || null,
      (promo.items || []).length,
    ]

    const items = (promo.items || []).filter((item) => item?.makat)
    if (!items.length) {
      // Promo with no items still appears in the file (Group B/C blank)
      rows.push([...promoCells, ...Array(headers.length - promoCells.length).fill(null)])
      continue
    }

    for (const item of items) {
      const reg = priceByMakat.get(item.makat) ?? null
      const row = [
        ...promoCells,
        item.makat,
        barcodeByMakat.get(item.makat) || '',
        item.name || '',
        item.boxSize ?? '',
        item.image || null,
        reg,
      ]
      for (let k = 1; k <= stepCount; k++) {
        const step = promo[`step_${k}`]
        const amount = num(step?.amount)
        const sp = num(step?.price)
        const discount = reg !== null && sp !== null ? reg - sp : null
        const discountPct = reg !== null && reg > 0 && sp !== null ? (1 - sp / reg) * 100 : null
        row.push(amount, sp, discount, discountPct)
      }
      rows.push(row)
    }
  }

  const ws = XLSX.utils.aoa_to_sheet([headers, ...rows])

  // Hyperlinks + number formats (data rows start at sheet row 1)
  rows.forEach((row, i) => {
    const r = i + 1
    for (const c of [PROMO_IMAGE_COL, ITEM_IMAGE_COL]) {
      const url = row[c]
      if (url) {
        ws[XLSX.utils.encode_cell({ r, c })] = {
          t: 's',
          v: url,
          l: { Target: url, Tooltip: 'פתח תמונה' },
        }
      }
    }
    setNumberFormat(XLSX, ws, r, MAX_QTY_COL, '0')
    setNumberFormat(XLSX, ws, r, ITEMS_COUNT_COL, '0')
    setNumberFormat(XLSX, ws, r, REGULAR_PRICE_COL, '0.00')
    for (let k = 0; k < stepCount; k++) {
      const base = STEP_BASE_COL + k * 4
      setNumberFormat(XLSX, ws, r, base, '0')
      setNumberFormat(XLSX, ws, r, base + 1, '0.00')
      setNumberFormat(XLSX, ws, r, base + 2, '0.00')
      setNumberFormat(XLSX, ws, r, base + 3, '0.0"%"')
    }
  })

  const cols = [
    { wch: 22 }, // מזהה מבצע
    { wch: 40 }, // שם המבצע
    { wch: 40 }, // תיאור
    { wch: 10 }, // מרכז
    { wch: 12 }, // סוג מבצע
    { wch: 8 }, // פעיל
    { wch: 8 }, // מאושר
    { wch: 14 }, // מצטרפים חדשים
    { wch: 10 }, // יום הזמנה
    { wch: 12 }, // הטבה שבועית
    { wch: 14 }, // הגבלת כמות
    { wch: 14 }, // תאריך התחלה
    { wch: 14 }, // תאריך סיום
    { wch: 40 }, // קישור תמונת מבצע
    { wch: 12 }, // מס׳ פריטים
    { wch: 12 }, // מק״ט
    { wch: 16 }, // ברקוד
    { wch: 40 }, // שם פריט
    { wch: 12 }, // גודל אריזה
    { wch: 40 }, // קישור תמונת פריט
    { wch: 18 }, // מחיר רגיל
  ]
  for (let k = 1; k <= stepCount; k++) {
    cols.push({ wch: 18 }, { wch: 16 }, { wch: 16 }, { wch: 12 })
  }
  ws['!cols'] = cols
  // Best-effort freeze of the header row (ignored by builds without pane support)
  ws['!freeze'] = { xSplit: 0, ySplit: 1 }

  const wb = XLSX.utils.book_new()
  XLSX.utils.book_append_sheet(wb, ws, 'מבצעים')
  wb.Workbook = { Views: [{ RTL: true }] }

  const now = new Date()
  const dateStr = [
    now.getFullYear(),
    String(now.getMonth() + 1).padStart(2, '0'),
    String(now.getDate()).padStart(2, '0'),
  ].join('-')
  const filename = `promotions-export-${opts.merakez || 'all'}-${dateStr}.xlsx`

  XLSX.writeFile(wb, filename)

  log.info('Promotions exported', { count: promos.length, filename })
  return promos.length
}
